import { useState } from "react";

import { PageHeader } from "../shell/PageHeader";
import { useRailFamily } from "../session/capabilities";
import {
  useGetV1Plants,
  useGetV1ReportsPreview,
  type EnvelopeReportPreviewData,
  type Figure,
  type LossCascadeRow,
  type Meta,
  type Reason,
  type ReportSection,
  type ReportSectionKey,
  type ReportSectionPart,
} from "@solarkey/api-client";
import {
  EmptyState,
  ErrorState,
  Metric,
  Skeleton,
  StateWord,
  isWithheld,
  reasonText,
  stateWord,
} from "@solarkey/ui";

import { displayLabel, engineOf, periodLabel } from "./SitePerformance";

/* ── Screen 08 (R1/R2): Reports: templates, schedules and viewer ──
   Every figure on this screen comes from `GET /v1/reports/preview`: the pack is ASSEMBLED
   by the server out of the panels that own each part (/v1/kpi for delivered energy, the
   17-row cascade, curtailment and the three named availabilities; /v1/interventions for the
   queue and its lanes; the site's own data path for what was actually seen).

   It is an assembly, NOT a run. This build has no report_template, report_run,
   report_artifact or approval table, so nothing here claims a pack was generated at a
   moment, approved by anyone, or reproducible from pinned snapshots:
     · the pane's "composed at" stamp is `meta.generated_at` — the SERVER's clock, never
       the browser's;
     · the templates panel, the schedule / "Next run" column and the run history keep their
       layout and withhold their content, because no store answers for them;
     · Approve and Open in Ask stay in place, honestly disabled, with the reason on the
       control itself (the house pattern — see SiteNow's "not wired" buttons).

   A section whose source does not exist arrives `withheld` and is rendered IN PLACE with
   its state word and the server's own reason. Dropping it would read as "this section had
   nothing to say"; narrating it would invent the content.

   Export is CSV only, built from the served sections. `data.export_formats` is ["csv"] —
   a PDF needs a renderer this build has not got, and a PDF assembled in the browser from
   strings would be a fabricated document that looks authoritative once it leaves the
   building. Nothing on this screen is computed: no KPI, no share, no cascade closure
   (CLAUDE.md rule 2). The screen prints what the server sent. */

/** The periods that resolve server-side without a from/to pair. `custom` is omitted: this
    screen collects no range, and the backend would fall back to a single day. */
const PERIOD_TABS: { key: string; label: string }[] = [
  { key: "today", label: "Today" },
  { key: "7d", label: "7D" },
  { key: "30d", label: "30D" },
  { key: "mtd", label: "Month" },
  { key: "fy", label: "FY" },
];

/** The whole-estate scope the endpoint names. Site scope is a site identifier. */
const SCOPE_ALL = "all";

/** The closed section vocabulary, as the pack names itself. */
const SECTION_TITLE: Record<ReportSectionKey, string> = {
  promise_and_delivery: "Promise and delivery",
  attainment_by_contract: "Attainment by contract",
  loss_cascade: "Loss cascade",
  interventions_and_recoveries: "Interventions and recoveries",
  curtailment_and_claims: "Curtailment and claims",
  availability_by_party: "Availability by party",
  data_quality: "Data quality",
};

/** What each served section actually carries — the panel's own description, not its content. */
const SECTION_SUB: Record<ReportSectionKey, string> = {
  promise_and_delivery: "delivered energy on its designated basis, against plan where a plan exists",
  attainment_by_contract: "per-contract attainment",
  loss_cascade: "the engine's rows, in the order it sent them",
  interventions_and_recoveries: "the detections and their lanes",
  curtailment_and_claims: "the curtailment rows of the cascade",
  availability_by_party: "the three named availabilities",
  data_quality: "what the site's own data path reports as seen",
};

/** The pack's figure names, as the owning panel keys them. */
const FIGURE_LABEL: Record<string, string> = {
  energy_delivered: "Energy delivered",
  energy_meter: "Energy, meter",
  energy_inverter_derived: "Energy, inverter-derived",
  reconciliation_ratio: "Meter against inverter sum",
  plan: "Plan",
  attainment_ratio: "Attainment of plan",
  availability_grid: "Grid availability",
  availability_technical: "Technical availability",
  availability_energy_by_cause: "Energy availability by cause",
  coverage: "Device coverage",
};

/** The named halves of a half-served section. */
const PART_LABEL: Record<string, string> = {
  claims: "Claims",
  by_party: "Attribution by party",
  recoveries: "Recoveries",
};

/* The closest reason templates the grammar carries for the three stores this build has
   not got. A report template, a schedule, a run and its artefact would all live in a
   document store, and an approval would need an acknowledgement store. Neither exists, so
   the panels withhold rather than list anything. (A dedicated template for each would read
   better — see the note handed back with this change; the grammar is not edited here.) */
// One template per case: "no document store" and "no acknowledgement store" were
// both true but neither named a REPORT store, which is the thing that is missing.
const NO_TEMPLATE_STORE: Reason = { template: "NOT_APPLICABLE_NO_REPORT_TEMPLATE_STORE" };
const NO_SCHEDULE_STORE: Reason = { template: "NOT_APPLICABLE_NO_REPORT_SCHEDULE_STORE" };
const NO_RUN_HISTORY: Reason = { template: "NOT_APPLICABLE_NO_REPORT_RUN_HISTORY" };
const NO_APPROVAL_STORE: Reason = { template: "NOT_APPLICABLE_NO_REPORT_APPROVAL_STORE" };

/** The template table's columns (wireframe 08). The layout survives; the rows have no source. */
const TEMPLATE_COLUMNS = ["Template", "Audience", "Cadence", "Scope", "Next run", ""];

export function Reports(): React.ReactNode {
  // The pack's scope and period are request parameters, not figures: the server resolves
  // the window and states it back in `meta.period`, which is the only window this screen
  // ever names.
  const [scope, setScope] = useState<string>(SCOPE_ALL);
  const [period, setPeriod] = useState<string>("30d");

  const pack = useGetV1ReportsPreview({ scope, period });
  // The scope picker's options: site identifiers as the server lists them (CLAUDE.md rule 5).
  const plants = useGetV1Plants({ period });
  // Ask is a family of its own: render the control from capabilities, never from a guess.
  const askFamily = useRailFamily("ask");

  const siteIds =
    plants.data?.status === 200
      ? (plants.data.data.data.items ?? [])
          .map((item) => item.site_id)
          .filter((id): id is string => typeof id === "string" && id.length > 0)
      : [];

  if (pack.isLoading) {
    return (
      <div className="flex flex-col gap-[14px]" aria-busy="true">
        <Skeleton variant="text" width="18rem" />
        <div className="sk-reports-cols">
          <Skeleton variant="row" lines={8} />
          <Skeleton variant="row" lines={8} />
        </div>
      </div>
    );
  }

  if (pack.isError || !pack.data || pack.data.status !== 200) {
    return (
      <ErrorState
        title="The report pack is unavailable"
        message="The pack could not be composed for this scope and period. Nothing is shown rather than a pack of stale or partial figures."
        onRetry={() => void pack.refetch()}
      />
    );
  }

  const env = pack.data.data;
  const d: EnvelopeReportPreviewData = env.data;
  const meta: Meta = env.meta;
  const sections = d.sections ?? [];
  const periodLine = periodLabel(meta.period);
  const composedAt = serverStamp(meta.generated_at);
  const served = sections.filter((s) => s.state === "served");
  const withheldSections = sections.filter((s) => s.state === "withheld");
  const canExportCsv = (d.export_formats ?? []).includes("csv");

  // Every half-served section's absent half, gathered so the pane can name them all.
  const withheldParts: { section: ReportSection; part: ReportSectionPart }[] = sections.flatMap(
    (s) => (s.parts ?? []).map((part) => ({ section: s, part })),
  );

  return (
    <div className="flex flex-col gap-[14px]">
      <PageHeader
        // The scope the SERVER resolved, as a site identifier or the whole estate.
        title="Reports"
        scope={`${d.scope} · ${periodLine}`}
        timeControl="none"
        action={
          <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
            <select
              aria-label="Pack scope"
              style={selectStyle}
              value={scope}
              onChange={(e) => setScope(e.target.value)}
            >
              <option value={SCOPE_ALL}>All sites</option>
              {siteIds.map((id) => (
                <option key={id} value={id}>{id}</option>
              ))}
            </select>
            <div className="sk-scenario" role="group" aria-label="Pack period">
              {PERIOD_TABS.map((t) => (
                <button
                  key={t.key}
                  type="button"
                  data-active={period === t.key ? "true" : "false"}
                  onClick={() => setPeriod(t.key)}
                >
                  {t.label}
                </button>
              ))}
            </div>
            {/* There is no run store, so this cannot create a run. It re-reads the pack for
                the chosen scope and period, and the label says exactly that. */}
            <button
              type="button"
              className="sk-btn sk-btn--primary"
              onClick={() => void pack.refetch()}
              disabled={pack.isFetching}
              title="Re-reads the pack for the selected scope and period. It does not create a run: this build has no report-run store."
            >
              {pack.isFetching ? "Re-composing…" : "Re-compose"}
            </button>
          </div>
        }
      />

      <div className="sk-reports-cols">
        {/* ── Templates and schedules ──
            The layout is the deliverable and stays. The content does not exist: no
            report_template, report_schedule or report_run table is in this build, so the
            agreed set, the cadence, the next run and the run history are withheld with
            their reason rather than listed from anywhere. */}
        <section aria-labelledby="rp-templates" className="sk-section">
          <div className="sk-section__head">
            <div>
              <h2 id="rp-templates" className="sk-section__title">Templates and schedules</h2>
              <span className="sk-section__sub">the agreed set, its cadence and its next run</span>
            </div>
            {/* Editing a template needs a template to edit, and the Ask family that the
                wireframe opens the editor in is a tier-gated family with no endpoint. */}
            <AskButton family={askFamily} label="Edit" />
          </div>
          <table className="sk-set-table">
            <thead>
              <tr>{TEMPLATE_COLUMNS.map((c, i) => <th key={i}>{c || "Actions"}</th>)}</tr>
            </thead>
            <tbody>
              <tr>
                <td colSpan={TEMPLATE_COLUMNS.length}>
                  <StateWord state="NOT_APPLICABLE" reason={NO_TEMPLATE_STORE} />
                </td>
              </tr>
            </tbody>
          </table>
          <p className="sk-set-note">
            No template, schedule or run is stored in this build, so no row can carry a name,
            an audience, a cadence or a next run. The pack beside this panel is composed on
            request from the panels that own each figure.
          </p>

          <h3 className="sk-sp-subhead">Run history</h3>
          <StateWord state="NOT_APPLICABLE" reason={NO_RUN_HISTORY} />
          <p className="sk-set-note">
            Nothing records that a pack was produced, so there is no history to list and no
            earlier pack to re-open.
          </p>

          <h3 className="sk-sp-subhead">Approval and distribution</h3>
          <StateWord state="NOT_APPLICABLE" reason={NO_APPROVAL_STORE} />
          <p className="sk-set-note">
            No approval is recorded against a pack and no recipient list exists, so a pack is
            never shown as a draft, as approved, or as sent.
          </p>
        </section>

        {/* ── The composed pack ── */}
        <section aria-labelledby="rp-pack" className="sk-section">
          <div className="sk-section__head">
            <div>
              <h2 id="rp-pack" className="sk-section__title">Report pack</h2>
              <span className="sk-section__sub">
                {d.scope} · {periodLine}
                {composedAt ? ` · composed by the server at ${composedAt}` : null}
              </span>
            </div>
          </div>
          <div className="sk-reports-kv">
            <div>
              <b>Period</b>
              {periodLine}
              {meta.period?.label ? ` · asked as ${meta.period.label}` : null}
            </div>
            <div>
              <b>Composed at</b>
              {/* `meta.generated_at` is required by the contract; if it is ever absent the
                  pane says so rather than reaching for the browser's clock. */}
              {composedAt ?? <StateWord state="NOT_MEASURED" reason={{ template: "NOT_MEASURED" }} />}
            </div>
            <div>
              <b>Sections</b>
              {served.length} of {sections.length} served, {withheldSections.length} withheld
              {withheldParts.length > 0 ? `, ${withheldParts.length} half-served` : null}
            </div>
            <div>
              <b>Provenance</b>
              {d.provenance?.rule_version ?? meta.rule_version}
              {d.provenance?.interface_version ? ` · ${d.provenance.interface_version}` : null}
              {` · ${(d.provenance?.intervention_refs ?? []).length} intervention references`}
              {(d.provenance?.snapshot_refs ?? []).length === 0
                ? " · no pinned snapshots, so the pack is not claimed reproducible later"
                : ` · ${(d.provenance?.snapshot_refs ?? []).length} snapshot references`}
            </div>
            <div>
              <b>Formats</b>
              {(d.export_formats ?? []).join(" ")} — the server offers no other, so no other
              is offered here
            </div>
            <div>
              <b>Figures</b>
              every figure carries its basis, state and reason exactly as the owning panel
              produced it; a withheld figure prints its state word, never a number
            </div>
          </div>

          {/* The pack outline: one line per section, each carrying its own state. This is a
              schematic of what the pack contains, not a rendering of a document body. */}
          <div className="sk-reports-preview" aria-label="Pack outline">
            {sections.map((s, i) => (
              <span
                key={s.key}
                className="sk-reports-preview__lbl"
                style={{ left: "5%", top: `${4 + i * 13}%` }}
              >
                {SECTION_TITLE[s.key] ?? s.key}
                {" · "}
                {s.state === "withheld" && s.withheld ? (
                  <StateWord state={s.withheld.state} reason={s.withheld.reason} />
                ) : (
                  "served"
                )}
              </span>
            ))}
          </div>

          <div className="sk-reports-actions">
            <button
              type="button"
              className="sk-btn sk-btn--primary"
              onClick={() => exportCsv(d, meta, periodLine)}
              disabled={!canExportCsv}
              title="Downloads the served sections as a CSV. A withheld figure exports its state word and its reason, never a number and never zero."
            >
              Download CSV
            </button>
            {/* No approval store and no endpoint: the control stays where the wireframe put
                it and says why it cannot act (the house pattern). */}
            <button
              type="button"
              className="sk-btn"
              disabled
              aria-disabled="true"
              title="Approving is not wired: this build records no approval against a pack and has no recipient list, so a pack can never be marked approved or sent."
            >
              Approve and send · not wired
            </button>
            <AskButton family={askFamily} label="Open in Ask" />
          </div>
        </section>
      </div>

      {/* ── The pack's contents, section by section, in the vocabulary's order ── */}
      {sections.length === 0 ? (
        <EmptyState
          variant="no_data"
          title="The pack has no sections"
          message="The server composed a pack with no sections at all, so there is nothing to state."
        />
      ) : (
        sections.map((s) => <PackSection key={s.key} section={s} />)
      )}
    </div>
  );
}

/* ── Open in Ask / Edit ──
   The Ask family has no endpoint in this build, and below Easy U it is not even unlocked.
   Either way the control is disabled; capabilities decide which reason it states, so the
   screen never navigates to a family as though it worked. */
function AskButton(
  { family, label }: { family: { locked: boolean; locked_tier?: string } | undefined; label: string },
): React.ReactNode {
  const title =
    family === undefined
      ? `${label} is not available: the Ask family is not in this session's capabilities.`
      : family.locked
        ? `${label} needs ${family.locked_tier ?? "a higher tier"}: the Ask family is locked for this session.`
        : `${label} is not wired: the platform serves no Ask endpoint, so there is nothing to open.`;
  return (
    <button type="button" className="sk-btn sk-btn--sm" disabled aria-disabled="true" title={title}>
      {label} · not wired
    </button>
  );
}

/* ── One pack section ──
   A withheld section keeps its place and prints the server's state word and reason, and
   nothing else: narrating it would invent the content it does not have. */
function PackSection({ section }: { section: ReportSection }): React.ReactNode {
  const id = `rp-sec-${section.key}`;
  const title = SECTION_TITLE[section.key] ?? section.key;

  if (section.state === "withheld") {
    return (
      <section aria-labelledby={id} className="sk-section">
        <div className="sk-section__head">
          <div>
            <h2 id={id} className="sk-section__title">{title}</h2>
          </div>
        </div>
        <StateWord
          state={section.withheld?.state ?? "NOT_MEASURED"}
          reason={section.withheld?.reason}
        />
      </section>
    );
  }

  return (
    <section aria-labelledby={id} className="sk-section">
      <div className="sk-section__head">
        <div>
          <h2 id={id} className="sk-section__title">{title}</h2>
          <span className="sk-section__sub">{SECTION_SUB[section.key]}</span>
        </div>
      </div>

      <FiguresTable figures={section.figures} />
      <CascadeTable rows={section.cascade} />
      <LanesTable lanes={section.lanes} />
      {section.key === "data_quality" ? (
        <>
          <HopsTable hops={section.hops} detail={section.detail} />
          <DevicesTable items={section.items} />
          <DataWindow detail={section.detail} />
        </>
      ) : (
        <InterventionsTable items={section.items} />
      )}
      <PartsList parts={section.parts} />
    </section>
  );
}

/* ── The named figures of a section, each through Metric ──
   Metric prints the state word and the reason for a withheld figure and a value with its
   own unit otherwise; no unit is written here (CLAUDE.md rule 3). */
function FiguresTable({ figures }: { figures?: Record<string, Figure> }): React.ReactNode {
  const entries = Object.entries(figures ?? {});
  if (entries.length === 0) return null;
  return (
    <table className="sk-set-table">
      <thead>
        <tr><th>Figure</th><th>Value</th><th>Basis</th></tr>
      </thead>
      <tbody>
        {entries.map(([name, f]) => (
          <tr key={name}>
            <td>{FIGURE_LABEL[name] ?? humanise(name)}</td>
            <td><Metric figure={f} showBasis={false} showProvenance={false} showFreshness={false} /></td>
            <td className="sk-set-from">{f.basis ?? ""}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/* ── The engine's cascade rows, in the order it sent them ──
   Nothing is derived: no closure check, no share of potential, no total. Each row's status
   and the engine it named as missing are the server's own. */
function CascadeTable({ rows }: { rows?: LossCascadeRow[] }): React.ReactNode {
  if (!rows || rows.length === 0) return null;
  const withheld = rows.filter((r) => !r.figure || isWithheld(r.figure));
  return (
    <>
      <table className="sk-set-table">
        <thead>
          <tr><th>Row</th><th>Value</th><th>Row status</th><th>Missing engine</th></tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.label}>
              <td>{displayLabel(r.label)}</td>
              <td>
                {r.figure ? (
                  <Metric figure={r.figure} showProvenance={false} showFreshness={false} />
                ) : (
                  <StateWord state="NOT_MEASURED" reason={{ template: "NOT_MEASURED_ENGINE_ABSENT" }} />
                )}
              </td>
              <td className="sk-set-from">{r.row_status ?? ""}</td>
              <td className="sk-set-from">{engineOf(r) ?? ""}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <p className="sk-set-note">
        {withheld.length} of {rows.length} rows carry no figure; each states why above. The
        share of potential and the lost-energy total are engine figures the contract does not
        serve, and may not be computed here, so the pack states neither.
      </p>
    </>
  );
}

/* ── The intervention lanes ──
   A lane with no lifecycle store carries a withheld count, which is printed as a state word.
   It is never 0: "nothing in this lane" and "this lane is not recorded" are different facts. */
function LanesTable({ lanes }: { lanes?: Record<string, unknown>[] }): React.ReactNode {
  if (!lanes || lanes.length === 0) return null;
  return (
    <table className="sk-set-table">
      <thead>
        <tr><th>Lane</th><th>Count</th><th>Energy at stake</th></tr>
      </thead>
      <tbody>
        {lanes.map((lane, i) => {
          const name = text(lane, "lane") ?? humanise(String(i));
          const n = num(lane, "count");
          const energy = fig(lane, "energy_at_stake");
          return (
            <tr key={name}>
              <td>{humanise(name)}</td>
              <td>
                {n === null ? (
                  <StateWord
                    state={(text(lane, "count_state") ?? "NOT_MEASURED") as Figure["state"]}
                    reason={reason(lane, "count_reason")}
                  />
                ) : (
                  n
                )}
              </td>
              <td>
                {energy ? (
                  <Metric figure={energy} showProvenance={false} showFreshness={false} />
                ) : null}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

/* ── The detections the queue holds ──
   Site identifiers only, never a plant or customer name (CLAUDE.md rule 5). */
function InterventionsTable({ items }: { items?: Record<string, unknown>[] }): React.ReactNode {
  if (!items || items.length === 0) return null;
  return (
    <>
      <h3 className="sk-sp-subhead">Detections <span>· as the queue holds them</span></h3>
      <table className="sk-set-table">
        <thead>
          <tr><th>Site</th><th>Class</th><th>Lane</th><th>Severity</th><th>Detection</th><th>Opened</th></tr>
        </thead>
        <tbody>
          {items.map((item, i) => (
            <tr key={text(item, "id") ?? i}>
              <td>{text(item, "site_id") ?? ""}</td>
              <td className="sk-set-from">{humanise(text(item, "class") ?? "")}</td>
              <td className="sk-set-from">{humanise(text(item, "lane") ?? "")}</td>
              <td className="sk-set-from">{humanise(text(item, "severity") ?? "")}</td>
              <td>
                <b>{text(item, "title") ?? ""}</b>
                {text(item, "what") ? ` · ${text(item, "what")}` : null}
              </td>
              <td className="sk-set-from">{serverStamp(text(item, "opened_at")) ?? ""}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </>
  );
}

/* ── The source chain, hop by hop, plus the hops the path itself withholds ── */
function HopsTable(
  { hops, detail }: { hops?: Record<string, unknown>[]; detail?: Record<string, unknown> },
): React.ReactNode {
  const withheldHops = rowsOf(detail, "hops_withheld");
  if ((!hops || hops.length === 0) && withheldHops.length === 0) return null;
  return (
    <>
      <h3 className="sk-sp-subhead">Source chain <span>· hop by hop</span></h3>
      <table className="sk-set-table">
        <thead>
          <tr><th>Hop</th><th>State</th><th>Last packet</th></tr>
        </thead>
        <tbody>
          {(hops ?? []).map((hop, i) => {
            const freshness = record(hop, "freshness");
            return (
              <tr key={text(hop, "source_kind") ?? i}>
                <td>{humanise(text(hop, "source_kind") ?? "")}</td>
                <td className="sk-set-from">{freshness ? (text(freshness, "state") ?? "") : ""}</td>
                <td className="sk-set-from">{serverStamp(text(hop, "last_packet")) ?? ""}</td>
              </tr>
            );
          })}
          {withheldHops.map((hop, i) => (
            <tr key={`held-${text(hop, "source_kind") ?? i}`}>
              <td>{humanise(text(hop, "source_kind") ?? "")}</td>
              <td colSpan={2}>
                <StateWord
                  state={(text(hop, "state") ?? "NOT_MEASURED") as Figure["state"]}
                  reason={reason(hop, "reason")}
                />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </>
  );
}

/* ── The devices the path itself reports as unseen ──
   Two kinds, and the server distinguishes them: a device with a signal list is instrumented
   but only partly reporting; one without carries no telemetry key at all. Neither is derived. */
function DevicesTable({ items }: { items?: Record<string, unknown>[] }): React.ReactNode {
  if (!items || items.length === 0) return null;
  return (
    <>
      <h3 className="sk-sp-subhead">Devices not fully seen</h3>
      <table className="sk-set-table">
        <thead>
          <tr><th>Device</th><th>Role</th><th>Signals not seen</th></tr>
        </thead>
        <tbody>
          {items.map((item, i) => {
            const missing = strings(item, "missing_signals");
            return (
              <tr key={text(item, "device_id") ?? i}>
                <td>{text(item, "device_id") ?? ""}</td>
                <td className="sk-set-from">{text(item, "role_name") ?? ""}</td>
                <td>
                  {missing.length > 0 ? (
                    missing.map(humanise).join(", ")
                  ) : (
                    <StateWord
                      state="NOT_APPLICABLE"
                      reason={{ template: "NOT_APPLICABLE_DEVICE_NOT_INSTRUMENTED" }}
                    />
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </>
  );
}

/** The coverage counts and the data window, exactly as the path stated them. */
function DataWindow({ detail }: { detail?: Record<string, unknown> }): React.ReactNode {
  if (!detail) return null;
  const counts = record(detail, "coverage_counts");
  const window = record(detail, "data_window");
  const reporting = counts ? num(counts, "reporting") : null;
  const monitored = counts ? num(counts, "monitored") : null;
  const earliest = window ? serverStamp(text(window, "earliest")) : null;
  const latest = window ? serverStamp(text(window, "latest")) : null;
  const statement = text(detail, "site_statement");
  if (reporting === null && earliest === null && !statement) return null;
  return (
    <p className="sk-set-note">
      {reporting !== null && monitored !== null
        ? `${reporting} of ${monitored} instrumented devices reported. `
        : null}
      {earliest && latest ? `The store holds data from ${earliest} to ${latest}. ` : null}
      {statement ? `The path states: ${humanise(statement)}.` : null}
    </p>
  );
}

/* ── The absent half of a half-served section ──
   Curtailment is served while claims are not; the three named availabilities are served
   while their attribution to a party is not; the queue is served while recoveries are not. */
function PartsList({ parts }: { parts?: ReportSectionPart[] }): React.ReactNode {
  if (!parts || parts.length === 0) return null;
  return (
    <ul className="sk-set-note">
      {parts.map((p) => (
        <li key={p.part}>
          {PART_LABEL[p.part] ?? humanise(p.part)} — <StateWord state={p.state} reason={p.reason} />
        </li>
      ))}
    </ul>
  );
}

/* ── CSV export ──
   The only format the server offers (`export_formats` is ["csv"]). Built from the served
   sections, with the server-resolved period, the rule version and the scope at the head, and
   every withheld figure exporting its STATE WORD where the number would be — never a number
   and never 0. The withheld sections and the withheld halves are listed by name with their
   reasons, so the file cannot be read as "these had nothing to say". */
function exportCsv(d: EnvelopeReportPreviewData, meta: Meta, periodLine: string): void {
  const prov = d.provenance ?? {};
  const sections = d.sections ?? [];
  const rows: string[][] = [
    ["Report pack", d.scope],
    ["Period", periodLine],
    ["Period asked as", meta.period?.label ?? ""],
    ["Composed at (server)", serverStamp(meta.generated_at) ?? ""],
    ["Rule version", prov.rule_version ?? meta.rule_version ?? ""],
    ["Interface version", prov.interface_version ?? ""],
    ["Snapshot references", (prov.snapshot_refs ?? []).join(" ")],
    ["Intervention references", (prov.intervention_refs ?? []).join(" ")],
    ["Export formats offered", (d.export_formats ?? []).join(" ")],
    ["Assembly", "Composed on request from the panels that own each figure. Not a stored run, not approved, not pinned to a snapshot."],
    [],
  ];

  const figureRows: string[][] = [];
  const cascadeRows: string[][] = [];
  const laneRows: string[][] = [];
  const detectionRows: string[][] = [];
  const hopRows: string[][] = [];
  const deviceRows: string[][] = [];
  const detailRows: string[][] = [];

  for (const s of sections) {
    if (s.state !== "served") continue;
    const title = SECTION_TITLE[s.key] ?? s.key;

    for (const [name, f] of Object.entries(s.figures ?? {})) {
      figureRows.push([
        title, FIGURE_LABEL[name] ?? humanise(name),
        figureCell(f), f.unit ?? "", f.basis ?? "", f.state, reasonText(f.reason) ?? "",
      ]);
    }

    for (const r of s.cascade ?? []) {
      cascadeRows.push([
        title, displayLabel(r.label), figureCell(r.figure), r.figure?.unit ?? "",
        r.figure?.state ?? "", String(r.row_status ?? ""), engineOf(r) ?? "",
        reasonText(r.figure?.reason) ?? "",
      ]);
    }

    for (const lane of s.lanes ?? []) {
      const n = num(lane, "count");
      const state = text(lane, "count_state") ?? "";
      laneRows.push([
        humanise(text(lane, "lane") ?? ""),
        n === null ? stateWord(state || "NOT_MEASURED") : String(n),
        state, reasonText(reason(lane, "count_reason")) ?? "",
      ]);
    }

    if (s.key === "data_quality") {
      for (const hop of s.hops ?? []) {
        const freshness = record(hop, "freshness");
        hopRows.push([
          humanise(text(hop, "source_kind") ?? ""),
          freshness ? (text(freshness, "state") ?? "") : "",
          serverStamp(text(hop, "last_packet")) ?? "", "",
        ]);
      }
      for (const hop of rowsOf(s.detail, "hops_withheld")) {
        const state = text(hop, "state") ?? "NOT_MEASURED";
        hopRows.push([
          humanise(text(hop, "source_kind") ?? ""),
          stateWord(state), "", reasonText(reason(hop, "reason")) ?? "",
        ]);
      }
      for (const item of s.items ?? []) {
        const missing = strings(item, "missing_signals");
        deviceRows.push([
          text(item, "device_id") ?? "", text(item, "role_name") ?? "",
          missing.length > 0 ? missing.join(" ") : stateWord("NOT_APPLICABLE"),
        ]);
      }
      const counts = record(s.detail, "coverage_counts");
      const window = record(s.detail, "data_window");
      if (counts) {
        detailRows.push(["Devices reporting", String(num(counts, "reporting") ?? "")]);
        detailRows.push(["Devices instrumented", String(num(counts, "monitored") ?? "")]);
      }
      if (window) {
        detailRows.push(["Data window earliest (server)", serverStamp(text(window, "earliest")) ?? ""]);
        detailRows.push(["Data window latest (server)", serverStamp(text(window, "latest")) ?? ""]);
      }
      const statement = text(s.detail, "site_statement");
      if (statement) detailRows.push(["Data path statement", humanise(statement)]);
    } else {
      for (const item of s.items ?? []) {
        detectionRows.push([
          text(item, "site_id") ?? "", humanise(text(item, "class") ?? ""),
          humanise(text(item, "lane") ?? ""), humanise(text(item, "severity") ?? ""),
          text(item, "title") ?? "", text(item, "what") ?? "",
          serverStamp(text(item, "opened_at")) ?? "",
        ]);
      }
    }
  }

  push(rows, ["Section", "Figure", "Value or state", "Unit", "Basis", "State", "Reason"], figureRows);
  push(rows, ["Section", "Cascade row", "Value or state", "Unit", "State", "Row status", "Missing engine", "Reason"], cascadeRows);
  push(rows, ["Intervention lane", "Count or state", "State", "Reason"], laneRows);
  push(rows, ["Site", "Class", "Lane", "Severity", "Detection", "Detail", "Opened at (server)"], detectionRows);
  push(rows, ["Data path hop", "Freshness", "Last packet (server)", "Reason"], hopRows);
  push(rows, ["Device", "Role", "Signals not seen"], deviceRows);
  push(rows, ["Data quality detail", "As the data path stated it"], detailRows);

  push(
    rows,
    ["Withheld section", "State", "Reason"],
    sections
      .filter((s) => s.state === "withheld")
      .map((s) => [
        SECTION_TITLE[s.key] ?? s.key,
        s.withheld ? stateWord(s.withheld.state) : stateWord("NOT_MEASURED"),
        reasonText(s.withheld?.reason) ?? "",
      ]),
  );

  push(
    rows,
    ["Withheld part", "Section", "State", "Reason"],
    sections.flatMap((s) =>
      (s.parts ?? []).map((p) => [
        PART_LABEL[p.part] ?? humanise(p.part),
        SECTION_TITLE[s.key] ?? s.key,
        stateWord(p.state),
        reasonText(p.reason) ?? "",
      ]),
    ),
  );

  // Not part of the pack, and named so the file cannot imply they exist.
  push(rows, ["Not in this build", "State", "Reason"], [
    ["Report templates", stateWord("NOT_APPLICABLE"), reasonText(NO_TEMPLATE_STORE) ?? ""],
    ["Schedules and next run", stateWord("NOT_APPLICABLE"), reasonText(NO_SCHEDULE_STORE) ?? ""],
    ["Run history", stateWord("NOT_APPLICABLE"), reasonText(NO_RUN_HISTORY) ?? ""],
    ["Approval and distribution", stateWord("NOT_APPLICABLE"), reasonText(NO_APPROVAL_STORE) ?? ""],
  ]);

  const csv = rows.map((r) => r.map(csvCell).join(",")).join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  // The scope is a site identifier (or the whole estate), never a plant or customer name.
  a.download = `report-pack-${d.scope}-${meta.period?.label ?? "period"}.csv`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

/** Append a titled block, and only when it has rows — an empty table is not a statement. */
function push(into: string[][], header: string[], body: string[][]): void {
  if (body.length === 0) return;
  into.push(header, ...body, []);
}

/** A withheld figure exports its STATE WORD where the number would be: never 0, never blank. */
function figureCell(f?: Figure): string {
  if (!f) return stateWord("NOT_MEASURED");
  if (isWithheld(f)) return stateWord(f.state);
  return String(f.value);
}

function csvCell(v: string): string {
  return /[",\n]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v;
}

/* ── Reading the server's open-shaped rows ──
   `items`, `lanes`, `hops` and `detail` are open objects in the contract, so every field is
   narrowed before it is printed. A field that is not there is not guessed at. */
function text(row: Record<string, unknown> | undefined, key: string): string | null {
  const v = row?.[key];
  return typeof v === "string" && v.length > 0 ? v : null;
}

function num(row: Record<string, unknown> | undefined, key: string): number | null {
  const v = row?.[key];
  return typeof v === "number" ? v : null;
}

function record(
  row: Record<string, unknown> | undefined, key: string,
): Record<string, unknown> | null {
  const v = row?.[key];
  return v !== null && typeof v === "object" && !Array.isArray(v)
    ? (v as Record<string, unknown>)
    : null;
}

function rowsOf(
  row: Record<string, unknown> | undefined, key: string,
): Record<string, unknown>[] {
  const v = row?.[key];
  return Array.isArray(v)
    ? v.filter((x): x is Record<string, unknown> => x !== null && typeof x === "object")
    : [];
}

function strings(row: Record<string, unknown> | undefined, key: string): string[] {
  const v = row?.[key];
  return Array.isArray(v) ? v.filter((x): x is string => typeof x === "string") : [];
}

function fig(row: Record<string, unknown> | undefined, key: string): Figure | undefined {
  const v = record(row, key);
  return v && typeof v["unit"] === "string" && typeof v["state"] === "string"
    ? (v as unknown as Figure)
    : undefined;
}

function reason(row: Record<string, unknown> | undefined, key: string): Reason | undefined {
  const v = record(row, key);
  return v && typeof v["template"] === "string" ? (v as unknown as Reason) : undefined;
}

/* The server's own ISO instant, with its offset kept and the fractional seconds dropped.
   The browser clock is never consulted and nothing is re-zoned: the pack was composed on the
   server, and only the server may say when. The fixture screen stamped this line from the
   browser's own clock, which read as a generation time nothing had recorded. */
function serverStamp(iso?: string | null): string | null {
  if (!iso) return null;
  return iso.replace(/\.\d+/, "");
}

/** A ruled key as a readable label. No label is invented: the key is the label. */
function humanise(key: string): string {
  if (!key) return "";
  return key.replace(/_/g, " ").replace(/^./, (c) => c.toUpperCase());
}

const selectStyle: React.CSSProperties = {
  height: 30,
  padding: "0 8px",
  fontSize: "12px",
  fontWeight: 500,
  border: "1px solid var(--sk-color-border-2)",
  borderRadius: "var(--sk-radius-control)",
  background: "var(--sk-color-surface-0)",
  color: "var(--sk-color-ink-1)",
  cursor: "pointer",
};
